import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/heading'
import * as Tabs from '@/components/ui/tabs'
import { Text } from '@/components/ui/text'
import { DialogContainer } from '@components/Dialog/DialogContainer'
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.min.css'
import { toaster } from '@/components/ui/toast'
import { RotateCcw, Save } from 'lucide-solid'
import { For, Show, createEffect, createSignal, onCleanup, onMount } from 'solid-js'
import { Box, Flex } from 'styled-system/jsx'
import { OEM, PSM, type Worker, createWorker } from 'tesseract.js'
import { Confirm } from './Confirm'
import { type DataArea, detectDataArea } from './detectDataArea'
import { CropZone } from './CropZone'
import { Scan } from './Scan'
import Trigger from './Trigger'
import { Upload } from './Upload'
import { extractHvfDetails } from './extractHvfDetails'
import { prepareForOcr } from './prepareForOcr'
import type { ScanedDetails } from './scannedDetails'

// Only what can appear in the data box: EXCHANGE:SYMBOL and PRICE-TIME lines
const OCR_WHITELIST = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.:-_! '

const ImageCropAndOCR = (props: {
  onConfirmDetails: (details: ScanedDetails, callback: () => void) => void
}) => {
  const [isOpen, setIsOpen] = createSignal(false)
  const [imageSrc, setImageSrc] = createSignal<string | undefined>()
  const [cropper, setCropper] = createSignal<Cropper | null>(null)
  const [ocrResult, setOcrResult] = createSignal('')
  const [ocrError, setOcrError] = createSignal(false)
  const [croppedPreview, setCroppedPreview] = createSignal<string>()
  const [isScanning, setIsScanning] = createSignal(false)
  const [isDetecting, setIsDetecting] = createSignal(false)

  // New state to cache the details from the Confirm component
  const [cachedDetails, setCachedDetails] = createSignal<ScanedDetails | null>(null)
  const [isFormValid, setIsFormValid] = createSignal(false)

  let imageRef: HTMLImageElement | undefined

  // Exchange/symbol may be missing if not in the crop; the user can fill them in on the Confirm tab
  const isConfirmDisabled = () => hvfDetails() === null

  onMount(() => {
    reset()
  })

  // Start loading the OCR engine as soon as the dialog opens, so it is ready by the time an image is uploaded
  createEffect(() => {
    if (isOpen()) {
      void getWorker()
    }
  })

  onCleanup(() => {
    clearCropper()
    terminateWorker()
  })

  // Loading the OCR engine + language data is the slowest part of a scan, so it is
  // started as soon as an image is uploaded and reused for every scan after that.
  let workerPromise: Promise<Worker> | null = null

  const getWorker = () => {
    if (!workerPromise) {
      workerPromise = createWorker('eng', OEM.LSTM_ONLY).then(async (worker) => {
        await worker.setParameters({
          tessedit_char_whitelist: OCR_WHITELIST,
          preserve_interword_spaces: '1',
          user_defined_dpi: '300',
        })
        return worker
      })
      workerPromise.catch(() => {
        workerPromise = null
      })
    }
    return workerPromise
  }

  const terminateWorker = () => {
    workerPromise?.then((worker) => worker.terminate()).catch(() => {})
    workerPromise = null
  }

  // Clean up existing cropper instance
  const clearCropper = () => {
    if (cropper()) {
      cropper()?.destroy()
      setCropper(null)
    }
  }

  const hvfDetails: () => ScanedDetails | null = () => {
    if (ocrResult() === '' || ocrResult() === 'processing' || ocrResult() === 'Error') {
      return null
    }

    const result = extractHvfDetails(ocrResult())

    if (result === null) {
      setOcrError(true)
    }

    return result
  }

  const initializeCropper = () => {
    if (imageRef) {
      const newCropper = new Cropper(imageRef, {
        aspectRatio: 0, // Freeform cropping
        viewMode: 1,
        scalable: true,
        zoomable: true,
        // No crop box until the data area has been detected, so users don't start selecting it themselves
        autoCrop: false,
        ready: () => autoSelectDataArea(newCropper),
      })
      setCropper(newCropper)
    }
  }

  // Move the crop box onto the HVF data box so the user only has to confirm it
  const autoSelectDataArea = async (currentCropper: Cropper) => {
    if (!imageRef) {
      return
    }

    const image = imageRef
    setIsDetecting(true)
    currentCropper.disable()

    let area: DataArea | null = null
    try {
      const worker = await getWorker()
      area = await detectDataArea(worker, image)
    } catch (error) {
      console.error('Error detecting HVF data area:', error)
    }

    // The image may have been replaced or reset while detecting
    if (cropper() !== currentCropper) {
      return
    }

    currentCropper.enable()
    currentCropper.crop()
    if (area) {
      currentCropper.setData(area)
    } else {
      // Best guess: the data box is drawn in the top right of the chart
      currentCropper.setData({
        x: image.naturalWidth * 0.6,
        y: 0,
        width: image.naturalWidth * 0.4,
        height: image.naturalHeight * 0.35,
      })
      toaster.info({
        closable: true,
        title: 'Data area not found',
        description: 'Adjust the crop area to the price/time list in the top right',
      })
    }
    setIsDetecting(false)
  }

  const handleCropAndOCR = async () => {
    setOcrError(false)
    setIsScanning(true)
    const currentCropper = cropper()
    if (!currentCropper) {
      setIsScanning(false)
      return
    }

    const croppedCanvas = currentCropper.getCroppedCanvas({
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    })
    setCroppedPreview(croppedCanvas.toDataURL())
    const ocrCanvas = prepareForOcr(croppedCanvas)

    setOcrResult('processing')
    try {
      const worker = await getWorker()

      // Single block works best for a tight crop; fall back to automatic layout
      // detection when the crop also includes chart labels, axis prices, etc.
      let text = ''
      for (const mode of [PSM.SINGLE_BLOCK, PSM.AUTO]) {
        await worker.setParameters({ tessedit_pageseg_mode: mode })
        const { data } = await worker.recognize(ocrCanvas)
        text = data.text
        if (extractHvfDetails(text) !== null) {
          break
        }
      }

      if (extractHvfDetails(text) === null) {
        console.warn('Could not parse HVF details from OCR result:', text)
        setOcrResult('')
        toaster.error({
          closable: true,
          title: 'Could not read the HVF details',
          description: 'Crop tightly around the price/time list in the top right and try again',
        })
      } else {
        setOcrResult(text)
      }
    } catch (error) {
      console.error('Error during OCR:', error)
      setOcrResult('')
      terminateWorker()
    } finally {
      setIsScanning(false)
    }
  }

  const onLoadImage = (imageUrl: string | undefined) => {
    setImageSrc(imageUrl)
    setIsDetecting(!!imageUrl)

    clearCropper()
  }

  const reset = () => {
    setIsDetecting(false)
    setImageSrc(undefined)
    setOcrResult('')
    cropper()?.destroy()
    setCropper(null)
    setOcrError(false)
    setCroppedPreview(undefined)
    setCachedDetails(null)
    setIsFormValid(false)
  }

  const options = [
    { id: 'upload', label: 'Upload' },
    { id: 'scan', label: 'Scan' },
    { id: 'confirm', label: 'Confirm' },
  ]

  const selectedTab = () => {
    if (hvfDetails() !== null) {
      return 'confirm'
    }

    if (imageSrc()) {
      return 'scan'
    }

    return 'upload'
  }

  return (
    <>
      <Trigger setIsOpen={setIsOpen} />
      <DialogContainer
        size="cover"
        isOpen={isOpen()}
        setIsOpen={setIsOpen}
      >
        <Tabs.Root
          size='xs'
          gap={3}
          defaultValue="upload"
          height='100%'
          display='grid'
          gridTemplateRows='auto 1fr'
          variant='enclosed'
          value={selectedTab()}
          {...props}
        >
          <Flex justifyContent='space-between' px={6} alignItems='center'>
            <Tabs.List justifySelf='start'>
              <For each={options}>
                {(option) => (
                  <Tabs.Trigger value={option.id}>
                    {option.label}
                  </Tabs.Trigger>
                )}
              </For>
              <Tabs.Indicator />
            </Tabs.List>
            <Flex gap={2}>
              <Show when={selectedTab() !== 'upload'}>
                <Button
                  colorPalette="gray"
                  variant="subtle"
                  onClick={reset}
                  size="xs"
                >
                  <RotateCcw />
                  <Text
                    display={{
                      base: 'block',
                      mdDown: 'none',
                    }}
                  >
                    Start again
                  </Text>
                </Button>
              </Show>
              <Show when={selectedTab() === 'confirm'}>
                <Button
                  colorPalette="jade"
                  variant="solid"
                  onClick={() => {
                    const details = cachedDetails()
                    if (details && isFormValid()) {
                      props.onConfirmDetails(details, () => {
                        setIsOpen(false)
                        toaster.info({
                          closable: true,
                          title: 'Success',
                          description: 'Extracted price data',
                        })
                      })
                    }
                  }}
                  disabled={!isFormValid() || !cachedDetails()}
                  size="xs"
                >
                  <Save />
                  <Text
                    display={{
                      base: 'block',
                      mdDown: 'none',
                    }}
                  >
                    Insert HVF Details
                  </Text>
                </Button>
              </Show>
            </Flex>
          </Flex>
          <Tabs.Content value="upload"
            alignContent='center'
            display='grid'
            height='100%'
            minHeight={0}
            justifyContent='center'>
            <Upload
              disabled={!!cropper()}
              onLoadImage={onLoadImage}
            />
          </Tabs.Content>
          <Tabs.Content
            value="scan"
            height='100%'
            display="grid"
            gap={4}
            gridTemplateRows="1fr auto"
          >
            <CropZone showCropper={!!imageSrc()} isDetecting={isDetecting()}>
              <img
                alt="hvf scan tool"
                ref={imageRef}
                src={imageSrc()}
                onLoad={initializeCropper}
                style={{ 'max-width': '650px' }}
              />
            </CropZone>
            <Scan
              onReset={reset}
              onScan={handleCropAndOCR}
              isDisabled={!cropper() || isScanning() || isDetecting()}
              isLoading={isScanning()}
              isDetecting={isDetecting()}
            />
          </Tabs.Content>
          <Tabs.Content value="confirm"
            display='grid'
            height='100%'
            minHeight={0}>
            <Show when={!isConfirmDisabled() && hvfDetails() !== null}>
              <Confirm
                preview={croppedPreview()}
                onChange={(values, isValid) => {
                  setCachedDetails(values)
                  setIsFormValid(isValid)
                }}
                isDisabled={!isConfirmDisabled()}
                details={hvfDetails()!}
              />
            </Show>
          </Tabs.Content>
        </Tabs.Root>
      </DialogContainer >
    </>
  )
}

export default ImageCropAndOCR
